import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BackArrowIcon } from "../../commonUI/Icons/SvgIcon";
import { toScreen } from "../../../js/modules/Routing";
import TopNav from "../../commonUI/TopHeader/TopHeader";

const SupportChat = ({ userDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, userId } = useParams();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);

  const { conversation, conversationType } = location.state || {};

  const actualConversationType =
    conversationType ||
    (type === "user"
      ? "user-user"
      : type === "support"
      ? "support"
      : "dispute");

  // Static chat data for fallback (development/testing)
  const getStaticChatData = (userId) => {
    const chatSessions = {
      C123456: {
        supportChatUser: {
          name: "John Smith",
          shortId: "C123456",
          avatar:
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
          type: "Client",
        },
        messages: [
          {
            id: 1,
            sender: "user",
            content: "It has been 20 minutes past the scheduled time",
            timestamp: "10:25",
            date: "Today",
          },
          {
            id: 2,
            sender: "admin",
            content:
              "I am sorry to hear that. Let me check the status of your driver right away.",
            timestamp: "10:30",
            date: "Today",
          },
          {
            id: 3,
            sender: "user",
            content:
              "I need help with my booking, the driver has not arrived yet.",
            timestamp: "10:15",
            date: "Today",
          },
          {
            id: 4,
            sender: "admin",
            content:
              "I understand your concern. Let me track your driver's location and provide you with an update.",
            timestamp: "10:35",
            date: "Today",
          },
          {
            id: 5,
            sender: "user",
            content: "Thank you for your help. I really appreciate it.",
            timestamp: "10:45",
            date: "Today",
          },
        ],
      },
      C785432: {
        supportChatUser: {
          name: "Emily Johnson",
          shortId: "C785432",
          avatar: "https://randomuser.me/api/portraits/women/44.jpg",
          type: "Admin",
        },
        messages: [
          {
            id: 1,
            sender: "user",
            content: "The driver didn't arrive at the pickup location.",
            timestamp: "14:40",
            date: "Today",
          },
          {
            id: 2,
            sender: "admin",
            content:
              "I'm sorry to hear about this issue. Let me immediately contact the driver and get this resolved for you.",
            timestamp: "14:45",
            date: "Today",
          },
          {
            id: 3,
            sender: "user",
            content: "I've been waiting for over 30 minutes now.",
            timestamp: "14:35",
            date: "Today",
          },
          {
            id: 4,
            sender: "admin",
            content:
              "I understand your frustration. I've contacted the driver and they should be there within 5 minutes.",
            timestamp: "14:50",
            date: "Today",
          },
        ],
      },
      C002134: {
        supportChatUser: {
          name: "David Wilson",
          shortId: "C002134",
          avatar: "https://randomuser.me/api/portraits/men/12.jpg",
          type: "Driver",
        },
        messages: [
          {
            id: 1,
            sender: "user",
            content: "I was charged twice for the same ride.",
            timestamp: "09:10",
            date: "Today",
          },
          {
            id: 2,
            sender: "admin",
            content:
              "I see the duplicate charge in your account. Let me process a refund for the extra charge immediately.",
            timestamp: "09:15",
            date: "Today",
          },
          {
            id: 3,
            sender: "user",
            content: "When will I receive the refund?",
            timestamp: "09:20",
            date: "Today",
          },
          {
            id: 4,
            sender: "admin",
            content:
              "The refund has been processed and should appear in your account within 3-5 business days.",
            timestamp: "09:25",
            date: "Today",
          },
        ],
      },
      C998877: {
        supportChatUser: {
          name: "Sophia Brown",
          shortId: "C998877",
          avatar: "https://randomuser.me/api/portraits/women/12.jpg",
          type: "Driver",
        },
        messages: [
          {
            id: 1,
            sender: "user",
            content: "The vehicle was not clean and had a strong odor.",
            timestamp: "11:45",
            date: "Today",
          },
          {
            id: 2,
            sender: "admin",
            content:
              "I apologize for this unpleasant experience. I will ensure the driver is notified and this issue is addressed immediately.",
            timestamp: "11:50",
            date: "Today",
          },
          {
            id: 3,
            sender: "user",
            content: "I expect a partial refund for this poor service.",
            timestamp: "11:55",
            date: "Today",
          },
          {
            id: 4,
            sender: "admin",
            content:
              "Absolutely, I will process a partial refund for you right away. You should see it reflected in your account soon.",
            timestamp: "12:00",
            date: "Today",
          },
        ],
      },
      C765432: {
        supportChatUser: {
          name: "Olivia Davis",
          shortId: "C765432",
          avatar: "https://randomuser.me/api/portraits/women/90.jpg",
          type: "Client",
        },
        messages: [
          {
            id: 1,
            sender: "user",
            content: "The driver was very rude during the trip.",
            timestamp: "08:30",
            date: "Today",
          },
          {
            id: 2,
            sender: "admin",
            content:
              "I'm very sorry to hear about this experience. This behavior is unacceptable and I will take immediate action.",
            timestamp: "08:35",
            date: "Today",
          },
          {
            id: 3,
            sender: "user",
            content: "I want to file a formal complaint.",
            timestamp: "08:40",
            date: "Today",
          },
          {
            id: 4,
            sender: "admin",
            content:
              "I will file a formal complaint against the driver and ensure appropriate disciplinary action is taken.",
            timestamp: "08:45",
            date: "Today",
          },
        ],
      },
    };

    return chatSessions[userId] || chatSessions["C123456"]; // Default to first user if not found
  };

  // Get chat data from props or fallback to static data
  const getChatData = () => {
    if (conversation && actualConversationType === "user-user") {
      // Handle user-user conversation from Conversations page
      return {
        client: conversation.client,
        driver: conversation.driver,
        messages: [
          {
            id: 1,
            sender: "client",
            content: conversation.message,
            timestamp: conversation.sentAt.time,
            date: conversation.sentAt.date,
          },
          {
            id: 2,
            sender: "driver",
            content:
              "Thank you for reaching out. I'm here to assist you with your request.",
            timestamp: "10:30",
            date: "Today",
          },
          {
            id: 3,
            sender: "client",
            content:
              "I appreciate your quick response. Looking forward to working with you.",
            timestamp: "10:32",
            date: "Today",
          },
          {
            id: 4,
            sender: "driver",
            content:
              "Absolutely! I'll make sure everything goes smoothly for you.",
            timestamp: "10:35",
            date: "Today",
          },
        ],
      };
    } else if (conversation) {
      // Use data passed from Support page (admin-user)
      const user =
        actualConversationType === "support"
          ? conversation?.supportChatUser
          : conversation?.disputeUser;
      const lastMessage =
        actualConversationType === "support"
          ? conversation?.supportChatMessage
          : conversation?.disputeMessage;

      return {
        supportChatUser: user,
        messages: [
          {
            id: 1,
            sender: "user",
            content: lastMessage,
            timestamp:
              actualConversationType === "support"
                ? conversation?.supportChatSentAt?.time
                : conversation?.disputeSentAt?.time,
            date:
              actualConversationType === "support"
                ? conversation?.supportChatSentAt?.date
                : conversation?.disputeSentAt?.date,
          },
          {
            id: 2,
            sender: "admin",
            content:
              "Hello! I'm here to help you with your inquiry. How can I assist you today?",
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            date: "Today",
          },
        ],
      };
    }

    // Fallback to static data
    return getStaticChatData(userId);
  };

  const [chatData] = useState(() => getChatData());
  const [messages, setMessages] = useState(chatData?.messages);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      const newMessage = {
        id: messages.length + 1,
        sender: actualConversationType === "user-user" ? "admin" : "admin",
        content: message,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        date: "Today",
      };

      setMessages((prev) => [...prev, newMessage]);
      setMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const renderMessage = (msg) => {
    if (actualConversationType === "user-user") {
      const isClient = msg?.sender === "client";
      const isDriver = msg?.sender === "driver";
      const isAdmin = msg?.sender === "admin";

      return (
        <div
          key={msg?.id}
          className={`chat-message ${
            isClient
              ? "client-message"
              : isDriver
              ? "driver-message"
              : "admin-message"
          }`}
        >
          <div className="message-content">
            <div className="message-avatar">
              {isClient && (
                <>
                  <img
                    src={chatData?.client?.avatar}
                    alt={chatData?.client?.name}
                    className="avatar-image"
                  />
                  <span className="sender-name">{chatData?.client?.name}</span>
                </>
              )}
              {isDriver && (
                <>
                  <img
                    src={chatData?.driver?.avatar}
                    alt={chatData?.driver?.name}
                    className="avatar-image"
                  />
                  <span className="sender-name">{chatData?.driver?.name}</span>
                </>
              )}
              {isAdmin && (
                <>
                  <span className="admin-badge">AS</span>
                  <span className="admin-label">Admin Support</span>
                </>
              )}
            </div>

            <div
              className={`message-bubble ${
                isClient
                  ? "client-bubble"
                  : isDriver
                  ? "driver-bubble"
                  : "admin-bubble"
              }`}
            >
              <p className="message-text">{msg?.content}</p>
            </div>
            <span className="message-time">{msg?.timestamp}</span>
          </div>
        </div>
      );
    } else {
      // Render admin-user conversation messages (existing functionality)
      const isAdmin = msg?.sender === "admin";

      return (
        <div
          key={msg?.id}
          className={`chat-message ${
            isAdmin ? "admin-message" : "user-message"
          }`}
        >
          <div className="message-content">
            {!isAdmin && (
              <div className="message-avatar">
                <img
                  src={chatData?.supportChatUser?.avatar}
                  alt={chatData?.supportChatUser?.name}
                  className="avatar-image"
                />
                {!isAdmin && (
                  <span className="sender-name">
                    {chatData?.supportChatUser?.name}
                  </span>
                )}
              </div>
            )}

            {isAdmin && (
              <div className="message-avatar">
                {isAdmin && <span className="admin-badge">AS</span>}
                {isAdmin && <span className="admin-label">Admin Support</span>}
              </div>
            )}
            <div
              className={`message-bubble ${
                isAdmin ? "admin-bubble" : "user-bubble"
              }`}
            >
              <p className="message-text">{msg?.content}</p>
            </div>
            <span className="message-time">{msg?.timestamp}</span>
          </div>
        </div>
      );
    }
  };

  // Get the display title based on conversation type
  const getDisplayTitle = () => {
    if (actualConversationType === "user-user") {
      return "User-User Conversation";
    }
    return "Support Chat";
  };

  // Get the back navigation based on conversation type
  const getBackNavigation = () => {
    if (actualConversationType === "user-user") {
      return () => navigate("/conversations");
    }
    return () => navigate("/support");
  };

  // Get the back button text based on conversation type
  const getBackButtonText = () => {
    if (actualConversationType === "user-user") {
      return "Back to Conversations";
    }
    return "Back to Support";
  };

  return (
    <>
      <TopNav title={getDisplayTitle()} />
      <div className="global-body">
        <div className="global-body-container">
          <button
            className="btn-outline-light icon-with-btn mb-16"
            onClick={getBackNavigation()}
          >
            <BackArrowIcon />
            {getBackButtonText()}
          </button>

          <div className="support-chat-container mt-16">
            {/* User Info */}
            <div className="chat-user-info">
              {actualConversationType === "user-user" ? (
                <>
                  <div
                    className="user-info"
                    onClick={(e) =>
                      toScreen(
                        e,
                        navigate,
                        `/client/${chatData?.client?.shortId}`
                      )
                    }
                  >
                    <img
                      src={chatData?.client?.avatar}
                      alt={chatData?.client?.name}
                      className="user-info-avatar"
                    />
                    <div className="user-info-details">
                      <h3 className="user-info-name">
                        {chatData?.client?.name}
                      </h3>
                      <span className="user-info-id">
                        {chatData?.client?.shortId}
                      </span>
                    </div>
                  </div>
                  <div
                    className="user-info"
                    onClick={(e) =>
                      toScreen(e, navigate, `/mav/${chatData?.driver?.shortId}`)
                    }
                  >
                    <img
                      src={chatData?.driver?.avatar}
                      alt={chatData?.driver?.name}
                      className="user-info-avatar"
                    />
                    <div className="user-info-details">
                      <h3 className="user-info-name">
                        {chatData?.driver?.name}
                      </h3>
                      <span className="user-info-id">
                        {chatData?.driver?.shortId}
                      </span>
                    </div>
                  </div>{" "}
                </>
              ) : (
                <div
                  className="user-info"
                  onClick={(e) =>
                    toScreen(
                      e,
                      navigate,
                      `/client/${chatData?.supportChatUser?.shortId}`
                    )
                  }
                >
                  <img
                    src={chatData?.supportChatUser?.avatar}
                    alt={chatData?.supportChatUser?.name}
                    className="user-info-avatar"
                  />
                  <div className="user-info-details">
                    <h3 className="user-info-name">
                      {chatData?.supportChatUser?.name}
                    </h3>
                    <span className="user-info-id">
                      {chatData?.supportChatUser?.shortId}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Messages Container */}
            <div className="messages-container">
              <div className="messages-wrapper">
                {messages?.map(renderMessage)}
                <div ref={messagesEndRef} />
              </div>
            </div>
            {/* Message Input */}{" "}
            {actualConversationType !== "user-user" && (
              <div className="message-input-container">
                <form onSubmit={handleSendMessage} className="message-form">
                  <div className="input-wrapper">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a message..."
                      className="message-input"
                      rows="1"
                    />
                    <button
                      type="submit"
                      className="send-button"
                      disabled={!message.trim()}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
            )}{" "}
          </div>
        </div>
      </div>
    </>
  );
};

SupportChat.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default SupportChat;
